import { Facts, FormSection, ReadOnlyNote, type FactRow } from "@omnabase/ui";
import { aiSummaryRows, helpCentreUrl, type Project } from "@omnabase/shared";
import { useNav, useWs } from "../../lib/nav";
import { useSession } from "../../shell/store";
import { inboxLabel } from "./ProjectPeople";
import { useForms } from "../../lib/forms";
import { useMailCard, type MailCard } from "./ProjectMail";

/** What a member reads instead of the administrators' controls (the founder, 2026-10-05, DECISIONS "Members in Settings"): what the
 *  admins set up, in a few lines, with nothing to press. General and AI show these without "Change AI settings", a project
 *  without "Manage project settings" on it. Each starts with the kit's read-only line (the polish round, 2026-10-31). */

const onOff = (x: unknown) => !!(x as { enabled?: boolean } | null | undefined)?.enabled;

function Head({ title, subtitle }: { title: string; subtitle: string }) {
  return <div className="ob-page__head"><div><h1 className="ob-page__title">{title}</h1><p className="ob-page__subtitle">{subtitle}</p></div></div>;
}

/** Settings › General for a member: the workspace's name, address, time zone and language (the workspace-wide hours left on 2026-11-01) */
export function GeneralSummary() {
  const { workspace } = useSession(); const s = workspace.settings;
  const rows = [
    { k: "Name", v: workspace.name },
    { k: "Address", v: `app.omnabase.com/${workspace.slug}` },
    { k: "Time zone", v: s.timezone },
    { k: "Language", v: s.language },
  ];
  return (
    <>
      <Head title="General" subtitle="What the workspace is set to." />
      <ReadOnlyNote />
      <div className="ob-form"><FormSection title="Workspace"><Facts rows={rows} /></FormSection></div>
    </>
  );
}

/** Settings › AI's Engine tab for a member (AI settings round one, 2026-10-18): the engine, the three switches, the investigation effort and
 *  the company-wide instructions' start, as facts rows like every other summary (the summary cards round, 2026-11-01: `aiSummaryRows`, On and
 *  Off as state words, no intro: the title says it); no balance or limit, no word of automatic replies. A paused key reads "(paused)". */
export function AiSummary() {
  const { workspace } = useSession();
  const rows = aiSummaryRows(workspace.settings, { ...workspace.plan.features, effective: workspace.effectiveEngine, keyProvider: workspace.aiKeyProvider });
  return (
    <>
      <ReadOnlyNote />
      <FormSection title="What the AI does"><Facts rows={rows} /></FormSection>
    </>
  );
}

/** The project summary's three mail rows (the polish round, 2026-10-31; the founder: "show the redirect and the default"), from the project's
 *  Mail read: where customers write (the forwarding address, else the project's own address), the project's own OmnaBase address, and where
 *  replies come from. The address alone is the value; its state is a quiet note (the summary cards round, 2026-11-01). */
export function mailRows(p: Project, card: MailCard | null): FactRow[] {
  const inbound = card?.inbound;
  const writeTo: FactRow = inbound
    ? { k: "Customers write to", v: inbound.address, note: inbound.state === "confirmed" ? "forwards here" : "not confirmed yet" }
    : { k: "Customers write to", v: p.address };
  const own: FactRow = p.archivedAt ? { k: "OmnaBase address", v: p.address, note: "not accepting mail" } : { k: "OmnaBase address", v: p.address };
  return [writeTo, own, { k: "Replies come from", v: card?.sending.address ?? "…" }];
}

/** A project the person does not manage: its name, its addresses, the public pages that are on, the way into its knowledge base */
export function ProjectSummary({ project: p }: { project: Project }) {
  const nav = useNav(); const { ws } = useWs();
  const { workspace } = useSession();
  const knowledge = ws(`/knowledge/articles?project=${p.id}`);
  const defaultForm = (useForms(workspace.slug).data ?? []).find((f) => f.id === p.defaultFormId && !f.archivedAt) ?? null;
  const card = useMailCard(workspace.slug, p.id);
  const rows: FactRow[] = [
    { k: "Name", v: p.name },
    ...mailRows(p, card.data ?? null),
    { k: "Help centre", v: onOff(p.helpCentre) ? helpCentreUrl(workspace.slug, p.slug) : "Off" },
    { k: "Default form", v: defaultForm ? defaultForm.name : "None" }, // the form builder, round three: the old web form became a form under Settings › Forms
    { k: "Knowledge base", title: "Open the knowledge base", v: <a className="ob-link" href={knowledge} onClick={(e) => nav(knowledge, e)}>Open the knowledge base</a> },
    { k: "Your queue", v: inboxLabel(workspace.me.projectInbox[p.id]) }, // 2026-10-17: what an administrator set for me on the People tab, read-only
  ];
  return (
    <main className="ob-page ob-settings__page" aria-label={p.name}>
      <Head title={p.name} subtitle="What this project is set to." />
      <ReadOnlyNote />
      <div className="ob-form"><FormSection title="Project"><Facts rows={rows} /></FormSection></div>
    </main>
  );
}
